import { useEffect, useState } from "react"
import { Plus, Bot, Play, Pause, StopCircle, TrendingUp, TrendingDown, Activity, Zap, Settings2, Trash2 } from "lucide-react"
import { useBot } from "../hooks/useBot"
import BotCard from "../components/bots/BotCard"
import BotBuilder from "../components/bots/BotBuilder"
import { PageLoader } from "../components/common/LoadingSpinner"
import Badge from "../components/common/Badge"
import { formatPct, formatCurrency } from "../utils/formatters"
import api from "../services/api"
import toast from "react-hot-toast"

const STATUS_COLORS = { active: "green", paused: "yellow", stopped: "gray", error: "red" }
const STATUS_ICONS = { active: Play, paused: Pause, stopped: StopCircle, error: StopCircle }

function BotRow({ bot, onAction }) {
  const [acting, setActing] = useState(false)
  const isUp = (bot.total_pnl_pct ?? 0) >= 0
  const Icon = STATUS_ICONS[bot.status] || Bot

  async function toggle() {
    setActing(true)
    try {
      if (bot.status === "active") {
        await api.patch(`/bots/${bot.id}`, { status: "paused" })
        toast.success(`${bot.name} paused`)
      } else {
        await api.patch(`/bots/${bot.id}`, { status: "active" })
        toast.success(`${bot.name} started`)
      }
      onAction()
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed") }
    finally { setActing(false) }
  }

  async function remove() {
    if (!confirm(`Delete bot "${bot.name}"? This cannot be undone.`)) return
    try {
      await api.delete(`/bots/${bot.id}`)
      toast.success(`${bot.name} deleted`)
      onAction()
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete") }
  }

  return (
    <div className="card hover:border-surface-300 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center ${bot.status === "active" ? "bg-trading-green/10" : "bg-surface-100"}`}>
            <Bot size={18} className={bot.status === "active" ? "text-trading-green" : "text-gray-400"} />
            {bot.status === "active" && <span className="absolute top-1 right-1 w-2 h-2 bg-trading-green rounded-full animate-ping" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white text-sm">{bot.name}</h3>
              <Badge variant={STATUS_COLORS[bot.status] || "gray"}>{bot.status}</Badge>
              {bot.mode === "paper" && <Badge variant="yellow">Paper</Badge>}
              {bot.mode === "live" && <Badge variant="red">Live</Badge>}
            </div>
            <p className="text-xs text-gray-400">{bot.symbol} · {bot.timeframe} · {bot.strategy_type}</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`font-mono font-bold ${isUp ? "text-trading-green" : "text-trading-red"}`}>
            {isUp ? "▲" : "▼"} {formatPct(Math.abs(bot.total_pnl_pct ?? 0))}
          </p>
          <p className="text-xs text-gray-500">{bot.total_trades ?? 0} trades</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          ["Win Rate", `${(bot.win_rate_pct ?? 0).toFixed(0)}%`, null],
          ["Total P&L", formatCurrency(bot.total_pnl_usd ?? 0), isUp],
          ["Open Pos.", bot.open_positions ?? 0, null],
          ["Mode", bot.mode || "paper", null],
        ].map(([k, v, pos]) => (
          <div key={k} className="bg-surface-100 rounded-lg p-2 text-center">
            <p className="text-[10px] text-gray-500 mb-0.5">{k}</p>
            <p className={`text-xs font-semibold ${pos === null ? "text-gray-300" : pos ? "text-trading-green" : "text-trading-red"}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={toggle} disabled={acting || bot.status === "error"} className={`flex-1 flex items-center justify-center gap-2 text-xs py-2 rounded-lg font-medium transition-all ${bot.status === "active" ? "bg-trading-yellow/10 text-trading-yellow hover:bg-trading-yellow/20 border border-trading-yellow/20" : "bg-trading-green/10 text-trading-green hover:bg-trading-green/20 border border-trading-green/20"}`}>
          {bot.status === "active" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Start</>}
        </button>
        <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1">
          <Settings2 size={12} /> Config
        </button>
        <button onClick={remove} className="p-2 rounded-lg bg-trading-red/10 text-trading-red hover:bg-trading-red/20 transition-colors border border-trading-red/10">
          <Trash2 size={13}/>
        </button>
      </div>
    </div>
  )
}

export default function Bots() {
  const { bots, loading, loadBots } = useBot()
  const [showBuilder, setShowBuilder] = useState(false)
  const [filter, setFilter] = useState("all")

  useEffect(() => { loadBots() }, [])

  const filtered = filter === "all" ? bots : bots.filter(b => b.status === filter)
  const active = bots.filter(b => b.status === "active")
  const totalPnl = bots.reduce((s, b) => s + (b.total_pnl_usd ?? 0), 0)
  const totalTrades = bots.reduce((s, b) => s + (b.total_trades ?? 0), 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Trading Bots</h2>
          <p className="text-sm text-gray-400 mt-1">{bots.length} bots · {active.length} active</p>
        </div>
        <button onClick={() => setShowBuilder(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> New Bot
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Bots", val: bots.length, color: "text-white", icon: Bot },
          { label: "Active Now", val: active.length, color: "text-trading-green", icon: Activity },
          { label: "Total Trades", val: totalTrades, color: "text-brand-400", icon: TrendingUp },
          { label: "Net P&L", val: formatCurrency(totalPnl), color: totalPnl >= 0 ? "text-trading-green" : "text-trading-red", icon: Zap },
        ].map(({ label, val, color, icon: Icon }) => (
          <div key={label} className="card-sm">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className={color} />
              <p className="text-xs text-gray-400">{label}</p>
            </div>
            <p className={`text-xl font-bold ${color}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      {bots.length > 0 && (
        <div className="flex gap-2">
          {["all","active","paused","stopped"].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === s ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{s}</button>
          ))}
        </div>
      )}

      {loading ? <PageLoader /> : bots.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Bot size={32} className="text-gray-500" />
          </div>
          <h3 className="font-semibold text-white text-lg mb-2">No bots yet</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">Create your first automated trading bot. Starts in safe paper mode — no real funds at risk.</p>
          <button onClick={() => setShowBuilder(true)} className="btn-primary">Create First Bot</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-gray-400 text-sm">No bots with status "{filter}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(b => <BotRow key={b.id} bot={b} onAction={loadBots} />)}
        </div>
      )}

      <BotBuilder isOpen={showBuilder} onClose={() => setShowBuilder(false)} />
    </div>
  )
}
